// src/admin-ops-sp/chatbot/ChatbotAdminComponents.js
import React, { useState, useEffect } from "react";
import {
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  CardHeader,
  Box,
  Container,
} from "@mui/material";
import Notification from "./Notification";
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  deleteDoc,
  onSnapshot,
} from "firebase/firestore";
import { firestore as db } from "../firebase.config";

// ---------- Keyword Manager ----------
export const KeywordManager = () => {
  const [newKeyword, setNewKeyword] = useState("");
  const [keywords, setKeywords] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "chatbot_keywords"),
      (snapshot) => {
        setKeywords(
          snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
        );
      },
    );
    return () => unsubscribe();
  }, []);

  const addKeyword = async () => {
    if (newKeyword.trim()) {
      await addDoc(collection(db, "chatbot_keywords"), {
        keyword: newKeyword.trim().toLowerCase(),
      });
      setNewKeyword("");
    }
  };

  const updateKeyword = async (id) => {
    await updateDoc(doc(db, "chatbot_keywords", id), {
      keyword: editValue.trim().toLowerCase(),
    });
    setEditingId(null);
  };

  const deleteKeyword = async (id) => {
    await deleteDoc(doc(db, "chatbot_keywords", id));
  };

  return (
    <Card
      sx={{
        borderRadius: 5,

        boxShadow: "0 6px 20px rgba(124,58,237,.05)",

        border: "1px solid #ECE9F4",

        background: "#1A0F2E",

        overflow: "hidden",
      }}
    >
      <CardHeader
        title={
          <Typography
            sx={{
              color: "#fff",
              fontWeight: 700,
              fontSize: 24,
            }}
          >
            Manage Chatbot Keywords
          </Typography>
        }
        sx={{
          background: "linear-gradient(135deg,#A78BFA,#7C3AED)",
          py: 2.5,
        }}
      />
      <CardContent sx={{ p: 4 }}>
        <Paper
          sx={{
            p: 2,
            mb: 4,
            background: "#FFFFFF",
            border: "1px solid #ECE9F4",
            boxShadow: "0 8px 20px rgba(124,58,237,.05)",
            borderRadius: 3,
            boxShadow: 0,
          }}
        >
          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: "#1A0F2E", fontWeight: 600 }}
          >
            Add New Keyword
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={8}>
              <TextField
                label="Keyword"
                value={newKeyword}
                onChange={(e) => setNewKeyword(e.target.value)}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 4,

                    background: "#fff",

                    "& fieldset": {
                      borderColor: "#E9D5FF",
                    },

                    "&:hover fieldset": {
                      borderColor: "#A78BFA",
                    },

                    "&.Mui-focused fieldset": {
                      borderColor: "#7C3AED",
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <Button
                variant="contained"
                sx={{
                  background: "linear-gradient(135deg,#A78BFA,#7C3AED)",
                  color: "#fff",
                  fontWeight: 700,
                  borderRadius: 3,
                  px: 4,
                  py: 1.2,
                  "&:hover": {
                    background: "linear-gradient(135deg,#7C3AED,#6D28D9)",
                  },
                }}
                fullWidth
                onClick={addKeyword}
              >
                Add
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <TableContainer
          component={Paper}
          sx={{ boxShadow: 0, borderRadius: 3 }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#F5F0FF" }}>
                <TableCell
                  sx={{ fontWeight: 600, color: "#1A0F2E", fontSize: 16 }}
                >
                  Keyword
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontWeight: 600, color: "#1A0F2E", fontSize: 16 }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {keywords.map((keyword) => (
                <TableRow
                  key={keyword.id}
                  sx={{
                    transition: ".2s",

                    "&:hover": {
                      background: "#FAF7FF",
                    },
                  }}
                >
                  <TableCell
                    sx={{ fontWeight: 500, color: "#333", fontSize: 15 }}
                  >
                    {editingId === keyword.id ? (
                      <TextField
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        size="small"
                        sx={{ bgcolor: "#f5f9ff", borderRadius: 2 }}
                      />
                    ) : (
                      keyword.keyword
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {editingId === keyword.id ? (
                      <Button
                        size="small"
                        sx={{ fontWeight: 600, borderRadius: 2, mr: 1 }}
                        onClick={() => updateKeyword(keyword.id)}
                      >
                        Save
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        sx={{
                          borderColor: "#7C3AED",

                          color: "#7C3AED",

                          "&:hover": {
                            background: "#F5F0FF",

                            borderColor: "#6D28D9",
                          },
                        }}
                      >
                        Edit
                      </Button>
                    )}
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      sx={{ fontWeight: 600, borderRadius: 2 }}
                      onClick={() => deleteKeyword(keyword.id)}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

// ---------- Question Manager ----------
export const QuestionManager = () => {
  const [newQuestion, setNewQuestion] = useState("");
  const [newSolution, setNewSolution] = useState("");
  const [questions, setQuestions] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [editSolution, setEditSolution] = useState("");

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "chatbot_questions"),
      (snapshot) => {
        setQuestions(
          snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
        );
      },
    );
    return () => unsubscribe();
  }, []);

  const addQuestion = async () => {
    if (newQuestion.trim()) {
      await addDoc(collection(db, "chatbot_questions"), {
        question: newQuestion.trim().toLowerCase(),
        answer: newSolution.trim(),
      });
      setNewQuestion("");
      setNewSolution("");
    }
  };

  const updateQuestion = async (id) => {
    await updateDoc(doc(db, "chatbot_questions", id), {
      question: editValue.trim().toLowerCase(),
      answer: editSolution.trim(),
    });
    setEditingId(null);
  };

  const deleteQuestion = async (id) => {
    await deleteDoc(doc(db, "chatbot_questions", id));
  };

  return (
    <Card
      sx={{
        borderRadius: 5,

        boxShadow: "0 6px 20px rgba(124,58,237,.05)",

        border: "1px solid #ECE9F4",

        background: "#1A0F2E",

        overflow: "hidden",
      }}
    >
      <CardHeader
        title={
          <Typography
            sx={{
              color: "#fff",
              fontWeight: 700,
              fontSize: 24,
            }}
          >
            Manage Chatbot Questions
          </Typography>
        }
        sx={{
          background: "linear-gradient(135deg,#A78BFA,#7C3AED)",
          py: 2.5,
        }}
      />
      <CardContent sx={{ p: 4 }}>
        <Paper
          sx={{
            p: 2,
            mb: 4,
            background: "#FFFFFF",
            border: "1px solid #ECE9F4",
            boxShadow: "0 8px 20px rgba(124,58,237,.05)",
            borderRadius: 3,
            boxShadow: 0,
          }}
        >
          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: "#1A0F2E", fontWeight: 600 }}
          >
            Add New Question
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Question"
                value={newQuestion}
                onChange={(e) => setNewQuestion(e.target.value)}
                fullWidth
                sx={{ bgcolor: "#e3f2fd", borderRadius: 2 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Solution"
                value={newSolution}
                onChange={(e) => setNewSolution(e.target.value)}
                fullWidth
                sx={{ bgcolor: "#e3f2fd", borderRadius: 2 }}
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              sx={{
                background: "linear-gradient(135deg,#A78BFA,#7C3AED)",
                color: "#fff",
                fontWeight: 700,
                borderRadius: 3,
                px: 4,
                py: 1.2,
                "&:hover": {
                  background: "linear-gradient(135deg,#7C3AED,#6D28D9)",
                },
              }}
              onClick={addQuestion}
            >
              Add
            </Button>
          </Box>
        </Paper>

        <TableContainer
          component={Paper}
          sx={{ boxShadow: 0, borderRadius: 3 }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#F5F0FF" }}>
                <TableCell
                  sx={{ fontWeight: 600, color: "#1A0F2E", fontSize: 16 }}
                >
                  Question
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 600, color: "#1A0F2E", fontSize: 16 }}
                >
                  Solution
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontWeight: 600, color: "#1A0F2E", fontSize: 16 }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {questions.map((question) => (
                <TableRow
                  key={question.id}
                  sx={{
                    transition: ".2s",

                    "&:hover": {
                      background: "#FAF7FF",
                    },
                  }}
                >
                  <TableCell
                    sx={{ fontWeight: 500, color: "#333", fontSize: 15 }}
                  >
                    {editingId === question.id ? (
                      <TextField
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        size="small"
                        sx={{ bgcolor: "#f5f9ff", borderRadius: 2 }}
                      />
                    ) : (
                      question.question
                    )}
                  </TableCell>
                  <TableCell>
                    {editingId === question.id ? (
                      <TextField
                        value={editSolution}
                        onChange={(e) => setEditSolution(e.target.value)}
                        size="small"
                        sx={{ bgcolor: "#f5f9ff", borderRadius: 2 }}
                      />
                    ) : (
                      question.answer || ""
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {editingId === question.id ? (
                      <Button
                        size="small"
                        variant="contained"
                        sx={{
                          background: "#7C3AED",

                          "&:hover": {
                            background: "#6D28D9",
                          },
                        }}
                        onClick={() => updateQuestion(question.id)}
                      >
                        Save
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        sx={{
                          borderColor: "#7C3AED",

                          color: "#7C3AED",

                          "&:hover": {
                            background: "#F5F0FF",

                            borderColor: "#6D28D9",
                          },
                        }}
                      >
                        Edit
                      </Button>
                    )}
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      sx={{ fontWeight: 600, borderRadius: 2 }}
                      onClick={() => deleteQuestion(question.id)}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

// ---------- Unanswered Questions ----------
export const UnansweredQuestions = () => {
  const [unanswered, setUnanswered] = useState([]);
  const [answerInputs, setAnswerInputs] = useState({});
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "unanswered_questions"),
      (snapshot) => {
        const newUnanswered = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        if (unanswered.length && newUnanswered.length > unanswered.length) {
          setNotification({
            open: true,
            message: "New unanswered question added!",
            severity: "info",
          });
        }
        setUnanswered(newUnanswered);
      },
    );
    return () => unsubscribe();
  }, [unanswered.length]);

  const handleInputChange = (id, value) => {
    setAnswerInputs((prev) => ({ ...prev, [id]: value }));
  };

  const handleSaveAnswer = async (id, question, answer) => {
    if (!answer || !answer.trim()) return;
    setLoading(true);

    const normalizedQuestion = question.trim().toLowerCase();

    await addDoc(collection(db, "chatbot_questions"), {
      question: normalizedQuestion,
      answer: answer.trim(),
    });
    await deleteDoc(doc(db, "unanswered_questions", id));

    setAnswerInputs((prev) => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });

    setLoading(false);
  };

  const handleDeleteUnanswered = async (id) => {
    setLoading(true);
    await deleteDoc(doc(db, "unanswered_questions", id));
    setLoading(false);
  };

  return (
    <>
      <Notification
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={() => setNotification({ ...notification, open: false })}
      />
      <Card
        sx={{
          borderRadius: 5,

          boxShadow: "0 6px 20px rgba(124,58,237,.05)",

          border: "1px solid #ECE9F4",

          background: "#1A0F2E",

          overflow: "hidden",
        }}
      >
        <CardHeader
          title={
            <Typography
              sx={{
                color: "#fff",
                fontWeight: 700,
                fontSize: 24,
              }}
            >
              Unanswered Questions
            </Typography>
          }
          sx={{
            background: "linear-gradient(135deg,#A78BFA,#7C3AED)",
            py: 2.5,
          }}
        />
        <CardContent sx={{ p: 4 }}>
          <TableContainer
            component={Paper}
            sx={{ boxShadow: 0, borderRadius: 3 }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "#F5F0FF" }}>
                  <TableCell
                    sx={{ fontWeight: 600, color: "#1A0F2E", fontSize: 16 }}
                  >
                    Question
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 600, color: "#1A0F2E", fontSize: 16 }}
                  >
                    Answer
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 600, color: "#1A0F2E", fontSize: 16 }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {unanswered.map((question) => (
                  <TableRow
                    key={question.id}
                    sx={{
                      transition: ".2s",

                      "&:hover": {
                        background: "#FAF7FF",
                      },
                    }}
                  >
                    <TableCell
                      sx={{ fontWeight: 500, color: "#333", fontSize: 15 }}
                    >
                      {question.question}
                    </TableCell>
                    <TableCell>
                      <TextField
                        value={answerInputs[question.id] || ""}
                        onChange={(e) =>
                          handleInputChange(question.id, e.target.value)
                        }
                        placeholder="Type answer..."
                        size="small"
                        fullWidth
                        disabled={loading}
                        sx={{ bgcolor: "#f5f9ff", borderRadius: 2 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        disabled={
                          loading ||
                          !(
                            answerInputs[question.id] &&
                            answerInputs[question.id].trim()
                          )
                        }
                        onClick={() =>
                          handleSaveAnswer(
                            question.id,
                            question.question,
                            answerInputs[question.id],
                          )
                        }
                        sx={{
                          background: "#7C3AED",

                          "&:hover": {
                            background: "#6D28D9",
                          },
                        }}
                      >
                        Save
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        variant="outlined"
                        size="small"
                        disabled={loading}
                        onClick={() => handleDeleteUnanswered(question.id)}
                        sx={{ borderRadius: 2, fontWeight: 600 }}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </>
  );
};

// ---------- Admin Dashboard ----------
export const ChatbotAdmin = () => (
  <Box
    sx={{
      p: { xs: 2, md: 4 },
      minHeight: "100vh",
      background: "linear-gradient(180deg,#FCFAFF 0%,#F5F0FF 100%)",
    }}
  >
    <Container maxWidth="md">
      <Box
        sx={{
          background: "linear-gradient(135deg,#A78BFA,#7C3AED)",
          borderRadius: 5,
          color: "#fff",
          p: 5,
          mb: 4,
          boxShadow: "0 18px 40px rgba(124,58,237,.2)",
        }}
      >
        <Typography variant="h3" fontWeight={700} >
          Chatbot Administration
        </Typography>
        <Typography>
          Manage chatbot keywords, questions/answers, and unanswered questions.
        </Typography>
      </Box>
      <Grid container direction="column" spacing={3}>
        <Grid item xs={12}>
          <KeywordManager />
        </Grid>
        <Grid item xs={12}>
          <QuestionManager />
        </Grid>
        <Grid item xs={12}>
          <UnansweredQuestions />
        </Grid>
      </Grid>
    </Container>
  </Box>
);
